import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import swal from "sweetalert";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import Pagination from "react-pagination-js";
import PaginationTextView from "../../components/common/PaginationTextView";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const isShipDatePassed = (shipDate) => {
  if (!shipDate) return false;
  const ship = new Date(shipDate);
  const today = new Date();
  ship.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return ship < today;
};

/**
 * Single source of truth for partial-payment logic.
 * remainingAmount from the API equals totalAmount before the first partial is paid,
 * so we compute the "true remaining" as totalAmount − minimumPartialAmt.
 */
const getPartialInfo = (invoice) => {
  if (invoice?.invoiceType !== "partial") return { isPartial: false };

  const partialPaid = !!invoice?.partialPaid;
  const partialMode = invoice?.partialMode || "percentage";
  const partialValue = Number(invoice?.partialValue ?? 0);
  const totalAmount = Number(
    invoice?.totalAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const minimumPartialAmt = Number(invoice?.minimumPartialAmount ?? 0);
  const paidAmount = Number(invoice?.paidAmount ?? 0);
  const trueRemaining = totalAmount - minimumPartialAmt;
  const amountToPay = partialPaid ? trueRemaining : minimumPartialAmt;
  const amountDueValue = invoice.totalAmountDue || invoice.amountDue || 0;
  const isPaid = Number(amountDueValue) === 0;

  return {
    isPartial: true,
    partialPaid,
    partialMode,
    partialValue,
    totalAmount,
    minimumPartialAmt,
    trueRemaining,
    paidAmount,
    amountToPay,
    fullyPaid: isPaid,
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ amountDue }) => {
  const isPaid = Number(amountDue) === 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${isPaid ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6]" : "bg-amber-50 text-amber-700 border-amber-200"}`}
    >
      <span
        className={`size-1.5 rounded-full ${isPaid ? "bg-[#0a7822a6]" : "bg-amber-500"}`}
      />
      {isPaid ? "Paid" : "Due"}
    </span>
  );
};

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const InfoRow = ({ label, value, valueClass = "text-gray-700" }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
    <p className="text-xs text-gray-600 font-medium">{label}</p>
    <p className={`text-sm font-semibold ${valueClass}`}>{value}</p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function QuickBookInvoiceListAdmin({ metaId }) {
  const user = useSelector((state) => state.user);
  const history = useNavigate();

  const isConnected = !!user?.quick_book_accesToken;

  const [TotalDataObj, setTotalDataObj] = useState({
    totalRecords: 0,
    totalDue: 0,
    totalPaid: 0,
    dueCount: 0,
    paidCount: 0,
  });
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("due");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [dots, setDots] = useState("");

  const limit = 50;

  // ── Debounce search ──
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchQuery(searchQuery);
    }, 600);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Loading dots animation ──
  useEffect(() => {
    let interval;
    if (loadingTotals)
      interval = setInterval(
        () => setDots((p) => (p.length >= 3 ? "" : p + ".")),
        400,
      );
    else setDots("");
    return () => clearInterval(interval);
  }, [loadingTotals]);

  // ── Fetch totals ──
  const fetchTotals = () => {
    setLoadingTotals(true);
    ApiClient.post("quickbook/invoice-total", {
      company: user?.quick_book_companyId,
    })
      .then((res) => {
        if (res.success) setTotalDataObj(res?.totals);
      })
      .catch(() => {})
      .finally(() => setLoadingTotals(false));
  };

  useEffect(() => {
    fetchTotals();
  }, []);

  // ── Fetch paginated list ──
  const fetchInvoices = async () => {
    loader(true);
    try {
      const res = await ApiClient.get(
        `quickbooks-docs/invoices/list?page=${currentPage}&limit=${limit}&status=${statusFilter}&search=${debouncedSearchQuery}`,
      );
      if (res?.success) {
        setInvoices(res?.data || []);
        setTotalCount(res?.total || res?.pagination?.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      loader(false);
      setInitialFetchDone(true);
    }
  };

  useEffect(() => {
    if (isConnected) fetchInvoices();
  }, [currentPage, statusFilter, debouncedSearchQuery]);

  // ── Open modal by metaId ──
  const fetchAndOpenInvoice = async (id) => {
    loader(true);
    try {
      const res = await ApiClient.get(`quickbooks-docs/invoices/list`, {
        id,
        limit: 1,
      });
      if (res?.success) {
        const found = (res?.data || []).find(
          (inv) =>
            inv._id === id ||
            inv.id === id ||
            inv.invoiceId?.toString() === id ||
            inv.qbId === id,
        );
        if (found) {
          setSelectedInvoice(found);
          setIsViewModalOpen(true);
        } else {
          swal({
            title: "Invoice Not Found",
            text: "The requested invoice could not be found.",
            icon: "error",
            button: "Close",
          });
          history(window.location.pathname, { replace: true });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      loader(false);
    }
  };

  useEffect(() => {
    if (!metaId || !initialFetchDone) return;
    const found = invoices.find(
      (inv) =>
        inv._id === metaId ||
        inv.id === metaId ||
        inv.invoiceId?.toString() === metaId ||
        inv.qbId === metaId,
    );
    if (found) {
      setSelectedInvoice(found);
      setIsViewModalOpen(true);
    } else fetchAndOpenInvoice(metaId);
  }, [metaId, invoices, initialFetchDone]);

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedInvoice(null);
    history(window.location.pathname, { replace: true });
  };

  const STATUS_TABS = [
    { label: "All", value: "", count: TotalDataObj?.totalRecords || 0 },
    { label: "Due", value: "due", count: TotalDataObj?.dueCount || 0 },
    { label: "Paid", value: "paid", count: TotalDataObj?.paidCount || 0 },
  ];

  // ── Not connected screen ──
  if (!isConnected) {
    return (
      <Layout>
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          <div className="flex flex-col gap-1 mb-6">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Invoices
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              Manage your QuickBooks invoices
            </p>
          </div>
          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
              <div className="size-20 rounded-2xl bg-[#2CA01C]/10 border border-[#2CA01C]/20 flex items-center justify-center">
                <svg
                  viewBox="0 0 40 40"
                  width="40"
                  height="40"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="10"
                    fill="none"
                    stroke="#2CA01C"
                    strokeWidth="3"
                  />
                  <line
                    x1="25"
                    y1="25"
                    x2="31"
                    y2="31"
                    stroke="#2CA01C"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold text-gray-900">
                  QuickBooks Not Connected
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  Connect your QuickBooks account to view and manage your
                  invoices seamlessly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => history("/quickbook/integration")}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Connect QuickBooks
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        {/* ── Stat cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[
            {
              label: "Total Invoices",
              value: TotalDataObj?.totalRecords || 0,
              bg: "bg-[#115B9D17]",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#115B9D"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
              ),
            },
            {
              label: "Total Amount Due",
              value: formatCurrency(TotalDataObj?.totalDue || 0),
              bg: "bg-amber-50",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#d97706"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              ),
            },
            {
              label: "Paid Invoices",
              value: TotalDataObj?.paidCount || 0,
              bg: "bg-[#F0FEF4]",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#008236"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ),
            },
          ].map((card, index) => (
            <div
              key={card.label}
              className={`border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4 ${
                index === 2 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div
                className={`size-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}
              >
                {card.icon}
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium">
                  {card.label}
                </p>

                <p className="text-xl font-semibold text-gray-900 mt-0.5">
                  {loadingTotals ? (
                    <span className="text-[10px] font-normal text-gray-600 italic">
                      collecting report{dots}
                    </span>
                  ) : (
                    card.value
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Table card ── */}
        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* Filters */}
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => {
                    setCurrentPage(1);
                    setStatusFilter(tab.value);
                  }}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${statusFilter === tab.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {tab.label}
                  <span
                    className={`text-[10px] font-semibold px-1.5 rounded-md ${statusFilter === tab.value ? "bg-gray-100 text-gray-600" : "bg-gray-200/70 text-gray-600"}`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-[350px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, invoice #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "5%" }} /> {/* Actions */}
                <col style={{ width: "9%" }} /> {/* Invoice */}
                <col style={{ width: "20%" }} /> {/* Customer */}
                <col style={{ width: "9%" }} /> {/* Type */}
                <col style={{ width: "9%" }} /> {/* Created */}
                <col style={{ width: "9%" }} /> {/* Due Date */}
                <col style={{ width: "9%" }} /> {/* Ship Date */}
                <col style={{ width: "7%" }} /> {/* Tax */}
                <col style={{ width: "14%" }} /> {/* Amount Due */}
                <col style={{ width: "9%" }} /> {/* Status */}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Invoice",
                    "Customer",
                    "Type",
                    "Created",
                    "Due Date",
                    "Ship Date",
                    "Tax",
                    "Amount Due",
                    "Status",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-2">
                      <span className="text-xs text-gray-500 font-medium truncate block">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">
                          No invoices found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map((item, index) => {
                    const amountDue =
                      item.amountDue ?? item.totalAmountDue ?? 0;
                    const isPaid = Number(amountDue) === 0;
                    const p = getPartialInfo(item);
                    const shipPassed = isShipDatePassed(item?.shipDate);

                    return (
                      <tr
                        key={item._id || item.invoiceId || index}
                        className={`transition-colors ${shipPassed ? "bg-red-50/60" : "hover:bg-gray-50/50"}`}
                      >
                        {/* View button */}
                        <td className="py-2 px-2">
                          <button
                            type="button"
                            onClick={() =>
                              history(
                                `/invoice/detail/${item._id || item.invoiceId || item.qbId}`,
                              )
                            }
                            title="View Details"
                            className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </td>

                        {/* Invoice # */}
                        <td className="py-2 px-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D] truncate max-w-full">
                            #{item.invoiceNumber}
                          </span>
                        </td>

                        {/* Customer — email now fully shown, no truncate */}
                        <td className="py-2 px-2 overflow-hidden">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="size-7 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-[9px] font-semibold text-[#115B9D]">
                                {getInitials(
                                  item.customerName || item.contact?.Name,
                                )}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {item.customerName || item.contact?.Name || "—"}
                              </p>
                              {item.contact?.Email && (
                                <p
                                  className="text-[10px] text-gray-600 break-all leading-tight"
                                  title={item.contact.Email}
                                >
                                  {item.contact.Email}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Invoice type badge */}
                        <td className="py-2 px-2">
                          {p.isPartial ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 truncate max-w-full">
                              <span className="size-1 rounded-full bg-amber-500 shrink-0" />
                              {p.partialPaid
                                ? "Partial paid"
                                : `Partial · ${p.partialMode === "percentage" ? `${p.partialValue}%` : "Fixed"}`}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600">
                              Standard
                            </span>
                          )}
                        </td>

                        <td className="py-2 px-2 text-xs text-gray-600 truncate">
                          {formatDate(item.createdAtQb || item.createTime)}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-600 truncate">
                          {formatDate(item.dueDate)}
                        </td>
                        <td className="py-2 px-2">
                          <span
                            className={`text-xs truncate block ${shipPassed ? "text-red-500 font-medium" : "text-gray-600"}`}
                          >
                            {formatDate(item.shipDate)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-500 truncate">
                          {formatCurrency(item.totalTax, item.currencyCode)}
                        </td>

                        {/* Amount due — shows partial breakdown if applicable */}
                        <td className="py-2 px-2 overflow-hidden">
                          {p.isPartial && !isPaid ? (
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <p className="text-xs font-semibold text-amber-600 truncate">
                                {formatCurrency(
                                  p.amountToPay,
                                  item.currencyCode,
                                )}
                                <span className="ml-1 text-[9px] font-normal text-gray-600">
                                  {p.partialPaid ? "remaining" : "due now"}
                                </span>
                              </p>
                              <p className="text-[10px] text-gray-600 truncate">
                                Total:{" "}
                                {formatCurrency(
                                  p.totalAmount,
                                  item.currencyCode,
                                )}
                              </p>
                            </div>
                          ) : (
                            <p
                              className={`text-xs font-semibold truncate ${isPaid ? "text-[#008236]" : "text-amber-600"}`}
                            >
                              {formatCurrency(amountDue, item.currencyCode)}
                            </p>
                          )}
                        </td>

                        <td className="py-2 px-2">
                          <StatusBadge amountDue={amountDue} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ================= Mobile Invoice Cards ================= */}

          <div className="block md:hidden space-y-4">
            {invoices.length === 0 ? (
              <div className="border border-gray-200 rounded-2xl bg-white py-12 px-6 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>

                <p className="text-sm text-gray-500">No invoices found</p>
              </div>
            ) : (
              invoices.map((item, index) => {
                const amountDue = item.amountDue ?? item.totalAmountDue ?? 0;

                const isPaid = Number(amountDue) === 0;

                const p = getPartialInfo(item);

                const shipPassed = isShipDatePassed(item?.shipDate);

                return (
                  <div
                    key={item._id || item.invoiceId || index}
                    className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                  >
                    {/* Header */}

                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                      <div>
                        <p className="text-xs text-gray-500">Invoice</p>

                        <p className="font-semibold text-[#115B9D]">
                          #{item.invoiceNumber}
                        </p>
                      </div>

                      <StatusBadge amountDue={amountDue} />
                    </div>

                    {/* Body */}

                    <div className="p-4 space-y-4">
                      {/* Customer */}

                      <div>
                        <div className="flex items-center gap-3">
                          <div className="size-11 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-[#115B9D]">
                              {getInitials(
                                item.customerName || item.contact?.Name,
                              )}
                            </span>
                          </div>

                          <div>
                            <p className="font-medium text-gray-800">
                              {item.customerName || item.contact?.Name || "—"}
                            </p>
                          </div>
                        </div>

                        {item.contact?.Email && (
                          <p className="text-xs text-gray-500 break-all pt-2">
                            {item.contact.Email}
                          </p>
                        )}
                      </div>

                      {/* Dates */}

                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-gray-200 p-3">
                          <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                            Created
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {formatDate(item.createdAtQb || item.createTime)}
                          </p>
                        </div>

                        <div className="rounded-xl border border-gray-200 p-3">
                          <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                            Due Date
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {formatDate(item.dueDate)}
                          </p>
                        </div>
                      </div>

                      {/* Ship Date */}

                      <div className="rounded-xl border border-gray-200 p-3">
                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                          Ship Date
                        </p>

                        <p
                          className={`text-sm font-semibold ${
                            shipPassed ? "text-red-500" : "text-gray-800"
                          }`}
                        >
                          {formatDate(item.shipDate)}
                        </p>
                      </div>

                      {/* Invoice Type */}

                      <div className="rounded-xl border border-gray-200 p-3">
                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-2">
                          Invoice Type
                        </p>

                        {p.isPartial ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                            <span className="size-1.5 rounded-full bg-amber-500"></span>

                            {p.partialPaid
                              ? "Partial Paid"
                              : `Partial ${
                                  p.partialMode === "percentage"
                                    ? `${p.partialValue}%`
                                    : "Fixed"
                                }`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                            Standard
                          </span>
                        )}
                      </div>

                      {/* Tax */}

                      <div className="rounded-xl border border-gray-200 p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase text-gray-500 font-medium">
                            Tax
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {formatCurrency(item.totalTax, item.currencyCode)}
                          </p>
                        </div>
                      </div>
                      {/* Amount Due */}

                      <div className="rounded-xl bg-[#115B9D]/5 border border-[#115B9D]/15 p-4">
                        {p.isPartial && !isPaid ? (
                          <div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">
                                Amount Due
                              </p>

                              <p className="text-lg font-bold text-amber-600">
                                {formatCurrency(
                                  p.amountToPay,
                                  item.currencyCode,
                                )}
                              </p>
                            </div>

                            <p className="text-xs text-gray-500 mt-1">
                              Total :{" "}
                              {formatCurrency(p.totalAmount, item.currencyCode)}
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">Amount Due</p>

                            <p
                              className={`text-lg font-bold ${
                                isPaid ? "text-[#008236]" : "text-amber-600"
                              }`}
                            >
                              {formatCurrency(amountDue, item.currencyCode)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* View Button */}

                      <button
                        type="button"
                        onClick={() =>
                          history(
                            `/invoice/detail/${
                              item._id || item.invoiceId || item.qbId
                            }`,
                          )
                        }
                        className="w-full h-11 rounded-xl bg-[#115B9D] text-white font-medium hover:bg-[#0d4d87] transition-all duration-200"
                      >
                        View Invoice
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-100 flex-wrap gap-3">
            <p className="text-gray-600 text-sm">
              {PaginationTextView(currentPage, limit, totalCount)}
            </p>
            <Pagination
              currentPage={currentPage}
              totalSize={totalCount}
              sizePerPage={limit}
              changeCurrentPage={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </div>

      {/* ── Invoice Detail Modal ─────────────────────────────────────────────── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeViewModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
                  {/* Close */}
                  <div className="px-2 py-2">
                    <button
                      onClick={closeViewModal}
                      className="z-10 min-w-[35px] w-[35px] h-[35px] ml-auto bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="overflow-y-auto scrollbar flex-1">
                    {/* ── Modal header ── */}
                    <div className="px-6 pb-5 text-center">
                      <div className="w-12 h-12 bg-[#115B9D] rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-2xl font-bold tracking-wide">
                        Invoice Details
                      </Dialog.Title>
                      <p className="mt-1 text-[11px] font-semibold tracking-[2px] text-[#115B9D] uppercase">
                        #{selectedInvoice?.invoiceNumber}
                      </p>
                    </div>

                    {selectedInvoice &&
                      (() => {
                        const amountDueRaw = Number(
                          selectedInvoice?.totalAmountDue ??
                            selectedInvoice?.amountDue ??
                            0,
                        );
                        const isPaid = amountDueRaw === 0;
                        const p = getPartialInfo(selectedInvoice);

                        // Invoice-only line items (no estimate bleed)
                        const invoiceLines = selectedInvoice?.raw?.Line || [];
                        const salesLines = invoiceLines.filter(
                          (l) => l.DetailType === "SalesItemLineDetail",
                        );
                        const discountLine = invoiceLines.find(
                          (item) => item?.DetailType === "DiscountLineDetail",
                        );
                        const discount = Number(
                          discountLine?.Amount || discountLine?.LineAmount || 0,
                        );
                        const subtotal = salesLines.reduce(
                          (s, l) => s + Number(l.Amount || 0),
                          0,
                        );
                        const tax = Number(selectedInvoice?.totalTax || 0);
                        const totalInvoice = Number(
                          selectedInvoice?.totalAmount ||
                            selectedInvoice?.raw?.TotalAmt ||
                            subtotal + tax,
                        );

                        // Fallback to estimate items if invoice lines are empty
                        const displayLines =
                          salesLines.length > 0
                            ? salesLines
                            : selectedInvoice?.estimate?.originalLineItems ||
                              selectedInvoice?.lineItems ||
                              [];

                        const billAddr =
                          selectedInvoice?.raw?.BillAddr ||
                          selectedInvoice?.BillAddr ||
                          selectedInvoice?.billingAddress;
                        const shipAddr =
                          selectedInvoice?.raw?.ShipAddr ||
                          selectedInvoice?.ShipAddr ||
                          selectedInvoice?.shippingAddress;

                        return (
                          <>
                            {/* ── Customer info ── */}
                            <div className="px-5 pb-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-3">
                                Customer
                              </p>
                              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                                  <span className="text-sm font-bold text-[#115B9D]">
                                    {getInitials(
                                      selectedInvoice.customerName ||
                                        selectedInvoice.contact?.Name,
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-800 capitalize">
                                    {selectedInvoice.customerName ||
                                      selectedInvoice.contact?.Name ||
                                      "—"}
                                  </p>
                                  {(selectedInvoice.contact?.Email ||
                                    selectedInvoice.raw?.BillEmail
                                      ?.Address) && (
                                    <p className="text-xs text-gray-500">
                                      {selectedInvoice.contact?.Email ||
                                        selectedInvoice.raw?.BillEmail?.Address}
                                    </p>
                                  )}
                                  {(selectedInvoice.contact?.Mobile ||
                                    selectedInvoice.contact?.Phone) && (
                                    <p className="text-xs text-gray-600">
                                      {selectedInvoice.contact?.Mobile ||
                                        selectedInvoice.contact?.Phone}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* ── Dates & status bar ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-3">
                                Invoice Info
                              </p>
                              <div className="border border-gray-200 rounded-xl grid grid-cols-4 items-stretch bg-[#115B9D08]">
                                {[
                                  {
                                    label: "Created",
                                    value: formatDate(
                                      selectedInvoice.createdAtQb ||
                                        selectedInvoice.createTime,
                                    ),
                                    color: "text-gray-800",
                                  },
                                  {
                                    label: "Due Date",
                                    value: formatDate(selectedInvoice.dueDate),
                                    color: "text-red-500",
                                  },
                                  {
                                    label: "Ship Date",
                                    value: formatDate(selectedInvoice.shipDate),
                                    color: isShipDatePassed(
                                      selectedInvoice.shipDate,
                                    )
                                      ? "text-red-500 font-bold"
                                      : "text-gray-800",
                                  },
                                ].map((item, i) => (
                                  <div
                                    key={item.label}
                                    className={`py-4 px-3 border-r border-gray-200`}
                                  >
                                    <p className="text-[9px] font-semibold tracking-widest text-gray-600 uppercase mb-1">
                                      {item.label}
                                    </p>
                                    <p
                                      className={`text-sm font-bold ${item.color}`}
                                    >
                                      {item.value}
                                    </p>
                                  </div>
                                ))}
                                <div className="py-4 px-3 flex flex-col items-center justify-center gap-1">
                                  <p className="text-[9px] font-semibold tracking-widest text-gray-600 uppercase mb-1">
                                    Status
                                  </p>
                                  <StatusBadge amountDue={amountDueRaw} />
                                </div>
                              </div>
                            </div>

                            {/* ── Partial payment schedule (admin read-only view) ── */}
                            {p.isPartial && (
                              <div className="px-5 py-4 border-b border-gray-100">
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">
                                    Payment Schedule
                                  </p>
                                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                    {p.partialMode === "percentage"
                                      ? `${p.partialValue}% upfront`
                                      : "Fixed upfront"}
                                  </span>
                                </div>

                                {/* Progress bar */}
                                <div className="mb-3">
                                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                    <span>Payment progress</span>
                                    <span>
                                      {p.fullyPaid
                                        ? "Fully paid"
                                        : p.partialPaid
                                          ? "Step 1 complete"
                                          : "Awaiting Step 1"}
                                    </span>
                                  </div>
                                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                                      style={{
                                        width: `${
                                          p.fullyPaid
                                            ? 100
                                            : p.partialPaid
                                              ? Math.round(
                                                  (p.minimumPartialAmt /
                                                    p.totalAmount) *
                                                    100,
                                                )
                                              : 0
                                        }%`,
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between text-[10px] text-gray-600 mt-1">
                                    <span>
                                      {formatCurrency(
                                        p.fullyPaid
                                          ? p.totalAmount
                                          : p.partialPaid
                                            ? p.minimumPartialAmt
                                            : 0,
                                      )}{" "}
                                      paid
                                    </span>
                                    <span>
                                      {formatCurrency(p.totalAmount)} total
                                    </span>
                                  </div>
                                </div>

                                {/* Step 1 */}
                                <div
                                  className={`flex items-start gap-3 p-3.5 rounded-t-xl border border-b-0 ${
                                    p.partialPaid
                                      ? "bg-[#F0FEF4] border-green-200"
                                      : "bg-amber-50 border-amber-200"
                                  }`}
                                >
                                  <div
                                    className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                      p.partialPaid
                                        ? "bg-[#008236]"
                                        : "bg-amber-500"
                                    }`}
                                  >
                                    {p.partialPaid ? <CheckIcon /> : "1"}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <p
                                        className={`text-sm font-semibold ${
                                          p.partialPaid
                                            ? "text-[#008236]"
                                            : "text-amber-700"
                                        }`}
                                      >
                                        {p.partialPaid
                                          ? "Step 1 — Partial received ✓"
                                          : "Step 1 — Partial payment (pending)"}
                                      </p>
                                      <p
                                        className={`text-sm font-bold ${
                                          p.partialPaid
                                            ? "text-[#008236]"
                                            : "text-amber-700"
                                        }`}
                                      >
                                        {formatCurrency(p.minimumPartialAmt)}
                                      </p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {p.partialMode === "percentage"
                                        ? `${p.partialValue}% of total invoice ${formatCurrency(p.totalAmount)}`
                                        : "Fixed partial amount as configured"}
                                    </p>
                                  </div>
                                </div>

                                {/* Step 2 */}
                                <div
                                  className={`flex items-start gap-3 p-3.5 rounded-b-xl border ${
                                    p.fullyPaid
                                      ? "bg-[#F0FEF4] border-green-200"
                                      : p.partialPaid
                                        ? "bg-[#EFF6FF] border-blue-200"
                                        : "bg-gray-50 border-gray-200"
                                  }`}
                                >
                                  <div
                                    className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                      p.fullyPaid
                                        ? "bg-[#008236]"
                                        : p.partialPaid
                                          ? "bg-[#115B9D]"
                                          : "bg-gray-300"
                                    }`}
                                  >
                                    {p.fullyPaid ? <CheckIcon /> : "2"}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <p
                                        className={`text-sm font-semibold ${
                                          p.fullyPaid
                                            ? "text-[#008236]"
                                            : p.partialPaid
                                              ? "text-[#115B9D]"
                                              : "text-gray-600"
                                        }`}
                                      >
                                        {p.fullyPaid
                                          ? "Step 2 — Remaining balance paid ✓"
                                          : p.partialPaid
                                            ? "Step 2 — Remaining balance (due)"
                                            : "Step 2 — Remaining balance (locked)"}
                                      </p>
                                      <p
                                        className={`text-sm font-bold ${
                                          p.fullyPaid
                                            ? "text-[#008236]"
                                            : p.partialPaid
                                              ? "text-[#115B9D]"
                                              : "text-gray-600"
                                        }`}
                                      >
                                        {formatCurrency(p.trueRemaining)}
                                      </p>
                                    </div>
                                    <p
                                      className={`text-xs mt-0.5 ${
                                        p.fullyPaid
                                          ? "text-[#008236]"
                                          : p.partialPaid
                                            ? "text-gray-500"
                                            : "text-gray-600"
                                      }`}
                                    >
                                      {p.fullyPaid
                                        ? "Invoice fully cleared. No balance remaining."
                                        : p.partialPaid
                                          ? "Customer owes this to fully clear the invoice."
                                          : "Unlocks once Step 1 is paid."}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ── Invoice items ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                                Invoice Items
                              </p>
                              <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-[#115B9D]">
                                    <tr>
                                      {[
                                        "#",
                                        "Description",
                                        "Qty",
                                        "Unit Price",
                                        "Amount",
                                      ].map((h, i) => (
                                        <th
                                          key={h}
                                          className={`py-2.5 px-4 text-[10px] font-semibold text-white tracking-wide ${i === 0 ? "text-left" : i < 2 ? "text-left" : "text-right"}`}
                                        >
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {displayLines.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="py-4 px-4 text-center text-sm text-gray-600"
                                        >
                                          No items
                                        </td>
                                      </tr>
                                    ) : (
                                      displayLines.map((li, i) => (
                                        <tr
                                          key={i}
                                          className="hover:bg-gray-50/60 transition-colors"
                                        >
                                          <td className="py-3 px-4 text-gray-600 w-8">
                                            {i + 1}
                                          </td>
                                          <td className="py-3 px-4 text-gray-700">
                                            {li.Description ||
                                              li.description ||
                                              li.SalesItemLineDetail?.ItemRef
                                                ?.name ||
                                              "—"}
                                          </td>
                                          <td className="py-3 px-4 text-right text-gray-600">
                                            {li.SalesItemLineDetail?.Qty ??
                                              li.qty ??
                                              "—"}
                                          </td>
                                          <td className="py-3 px-4 text-right text-gray-600">
                                            {formatCurrency(
                                              li.SalesItemLineDetail
                                                ?.UnitPrice ?? li.unitPrice,
                                              selectedInvoice.currencyCode,
                                            )}
                                          </td>
                                          <td className="py-3 px-4 text-right font-semibold text-gray-800">
                                            {formatCurrency(
                                              li.Amount ??
                                                li.LineAmount ??
                                                li.amount,
                                              selectedInvoice.currencyCode,
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* ── Summary ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                                Financial Summary
                              </p>
                              <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Subtotal
                                  </p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    {formatCurrency(
                                      subtotal,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Tax (
                                    {selectedInvoice?.raw?.TxnTaxDetail
                                      ?.TaxLine?.[0]?.TaxLineDetail
                                      ?.TaxPercent ?? ""}
                                    %)
                                  </p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    {formatCurrency(
                                      tax,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Discount
                                  </p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    -
                                    {formatCurrency(
                                      discount,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Invoice Total
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {formatCurrency(
                                      totalInvoice,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>

                                {/* Partial breakdown */}
                                {p.isPartial && !isPaid && (
                                  <>
                                    <div className="flex items-center justify-between px-4 py-3 bg-amber-50">
                                      <div>
                                        <p className="text-sm text-amber-700 font-medium">
                                          {p.partialPaid
                                            ? "Step 1 received"
                                            : `Step 1 — Due now (${p.partialMode === "percentage" ? `${p.partialValue}%` : "fixed"})`}
                                        </p>
                                        <p className="text-xs text-amber-600 mt-0.5">
                                          {p.partialPaid
                                            ? "Already collected"
                                            : "Awaiting customer payment"}
                                        </p>
                                      </div>
                                      <p className="text-sm font-bold text-amber-700">
                                        {formatCurrency(
                                          p.minimumPartialAmt,
                                          selectedInvoice?.currencyCode,
                                        )}
                                      </p>
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-3 bg-blue-50/50">
                                      <div>
                                        <p
                                          className={`text-sm font-medium ${p.partialPaid ? "text-[#115B9D]" : "text-gray-600"}`}
                                        >
                                          {p.partialPaid
                                            ? "Step 2 — Balance still owed"
                                            : "Step 2 — Remaining (locked)"}
                                        </p>
                                        <p className="text-xs text-gray-600 mt-0.5">
                                          {p.partialPaid
                                            ? "Customer must pay to clear invoice"
                                            : "Unlocks after Step 1"}
                                        </p>
                                      </div>
                                      <p
                                        className={`text-sm font-bold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-600"}`}
                                      >
                                        {formatCurrency(
                                          p.trueRemaining,
                                          selectedInvoice?.currencyCode,
                                        )}
                                      </p>
                                    </div>
                                  </>
                                )}

                                {isPaid && (
                                  <div className="flex items-center justify-between px-4 py-3 bg-[#F0FEF4]">
                                    <p className="text-sm text-[#008236] font-medium">
                                      Total Collected
                                    </p>
                                    <p className="text-sm font-bold text-[#008236]">
                                      {formatCurrency(
                                        totalInvoice,
                                        selectedInvoice?.currencyCode,
                                      )}
                                    </p>
                                  </div>
                                )}

                                {/* Bottom total */}
                                <div className="flex items-center justify-between px-4 py-3.5 bg-[#115B9D] rounded-b-xl">
                                  <p className="text-sm font-bold text-white tracking-wide">
                                    {isPaid
                                      ? "Fully Paid ✓"
                                      : p.isPartial
                                        ? p.partialPaid
                                          ? "Balance Due (Step 2)"
                                          : "Due Now (Step 1)"
                                        : "Total Due"}
                                  </p>
                                  <p className="text-base font-bold text-white">
                                    {isPaid
                                      ? formatCurrency(
                                          totalInvoice,
                                          selectedInvoice?.currencyCode,
                                        )
                                      : formatCurrency(
                                          p.isPartial
                                            ? p.amountToPay
                                            : amountDueRaw,
                                          selectedInvoice?.currencyCode,
                                        )}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* ── Markup info (if present) ── */}
                            {selectedInvoice?.markup?.type && (
                              <div className="px-5 py-4 border-b border-gray-100">
                                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-2.5">
                                  Markup
                                </p>
                                <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center justify-between">
                                  <p className="text-sm text-gray-600 capitalize">
                                    {selectedInvoice.markup.type} markup
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {selectedInvoice.markup.type ===
                                    "percentage"
                                      ? `${selectedInvoice.markup.value}%`
                                      : formatCurrency(
                                          selectedInvoice.markup.value,
                                        )}
                                    {selectedInvoice.markup.amount
                                      ? ` (${formatCurrency(selectedInvoice.markup.amount)})`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* ── Addresses ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-3">
                                Addresses
                              </p>
                              <div className="flex gap-4 flex-wrap sm:flex-nowrap">
                                {[
                                  { title: "📍 Billing", addr: billAddr },
                                  { title: "🚚 Shipping", addr: shipAddr },
                                ].map(({ title, addr }) => (
                                  <div
                                    key={title}
                                    className="flex-1 min-w-[180px] bg-white border border-[#115B9D]/20 p-4 rounded-xl"
                                  >
                                    <h2 className="text-xs font-semibold text-[#115B9D] border-b border-[#115B9D]/20 pb-2 mb-2 uppercase tracking-wide">
                                      {title}
                                    </h2>
                                    <div className="space-y-0.5 text-sm text-gray-600">
                                      {[
                                        addr?.Line1,
                                        addr?.City || addr?.Line2,
                                        addr?.State,
                                        addr?.Country || addr?.Line3,
                                        addr?.PostalCode,
                                      ]
                                        .filter(Boolean)
                                        .map((line, i) => (
                                          <p key={i}>{line}</p>
                                        ))}
                                      {!addr?.Line1 && (
                                        <p className="text-gray-600 text-xs italic">
                                          No address on file
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* ── Private note (QB memo) ── */}
                            {selectedInvoice?.raw?.PrivateNote && (
                              <div className="px-5 py-4 border-b border-gray-100">
                                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
                                  Private Note / Tracking
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800 font-mono">
                                  {selectedInvoice.raw.PrivateNote}
                                </div>
                              </div>
                            )}

                            {/* ── QB online payment links ── */}
                            {/* {selectedInvoice?.raw?.InvoiceLink && (
                              <div className="px-5 py-4 border-b border-gray-100">
                                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
                                  QuickBooks Invoice Link
                                </p>
                                <a
                                  href={selectedInvoice.raw.InvoiceLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-[#115B9D] hover:underline font-medium"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                  </svg>
                                  View on QuickBooks
                                </a>
                              </div>
                            )} */}
                          </>
                        );
                      })()}
                  </div>

                  {/* ── Footer ── */}
                  <div className="bg-[#115B9D] px-5 py-3.5 shrink-0">
                    <p className="text-center text-[11px] text-white">
                      © 2026 · All rights reserved · Admin View
                    </p>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
